import {
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
  UnknownAction
} from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import { IErrorData } from '$/types'

import { setPopupNotification } from 'features/Settings/settingsSlice'

// Ripped from ErrorCodesRespository.cs + AssessmentValidator.cs
export enum ErrorCode {
  Null = 700,
  InvalidLength = 701,
  IllegalCharacters = 702,
  NotUnique = 703,
  ProfileNameNotUnique = 704,
  InvalidQueryParameter = 705,
  ExceededMaxDatasetIds = 706,
  ProfileNotFound = 707,
  MissingBaselineId = 708,
  AssessmentNameNotUnique = 709,
  IllegalParameterUsage = 710,
  MissingLineId = 711,
  AssessmentNotRunning = 712,
  UnAuthorized = 713,
  RiskCalculationAlreadyProcess = 715,
  InvalidIdValue = 714
}

const notificationMiddleware: Middleware =
  (store: MiddlewareAPI) => (next) => (action) => {
    const untypedAction = action as UnknownAction
    let catched = false

    if (untypedAction.error || isRejectedWithValue(untypedAction)) {
      const castedAction = untypedAction as {
        payload?: {
          status: number
        }
      }

      const errorCode = castedAction.payload?.status
      let title = ''
      let message = ''

      // Catch error codes that require popup notification
      switch (errorCode) {
        case 403:
          title = 'Access Denied'

          message = `You don't have the proper permissions to access this module. Speak
          with your administrator if you require access.`
          showPopupNotification(store, title, message)
          catched = true

          break
      }
    }

    // Redux API Middleware
    if (untypedAction.error === true && !catched) {
      const castedAction = untypedAction as {
        payload?: {
          response?: {
            errors: IErrorData[]
          }
        }
      }

      const errors = castedAction.payload?.response?.errors

      showErrorNotification({ errors, source: untypedAction.type })
    }

    // RTK Query
    if (isRejectedWithValue(untypedAction) && !catched) {
      const castedAction = untypedAction as {
        payload?: {
          data?: unknown
          status?: number
        }
        meta?: {
          arg?: {
            endpointName?: string
          }
          baseQueryMeta?: {
            request?: {
              url?: string
            }
          }
        }
      }

      const res = castedAction.payload?.data
      // hard to juggle with primative and object type so falling back to any
      const errors = (res as { errors?: IErrorData[] } | undefined)?.errors ?? res
      const endpointName = castedAction.meta?.arg?.endpointName
      const source = castedAction.meta?.baseQueryMeta?.request?.url
      const hideApiDetails = isWISAssessmentEndpoint(endpointName, source)

      if (
        !(
          endpointName === 'getColorByValueConfig' &&
          castedAction.payload?.status === 404
        )
      ) {
        // include some conditions to bypass error warnings above
        // TODO: update BE API to return empty object instead

        showErrorNotification({
          errors,
          source,
          includeApiDetails: !hideApiDetails
        })
      }
    }

    return next(untypedAction)
  }

interface IAPIErrorToastCreator {
  errors?: unknown
  source?: string
  includeApiDetails?: boolean
}

const isWISAssessmentEndpoint = (
  endpointName?: string,
  source?: string
): boolean => {
  if (
    endpointName === 'addQRAAssessment' ||
    endpointName === 'patchQRAAssessment'
  ) {
    return true
  }

  return source?.includes('/assessments') ?? false
}

const showPopupNotification = (
  store: MiddlewareAPI,
  title: string,
  message: string
): void => {
  store.dispatch({
    type: setPopupNotification.type,
    payload: {
      isOpen: true,
      title: title,
      message: message
    }
  })
}

const showErrorNotification = ({
  errors,
  source,
  includeApiDetails = true
}: IAPIErrorToastCreator): void => {
  if (Array.isArray(errors)) {
    errors.forEach((rawError) => {
      const e = rawError as IErrorData

      switch (e.code) {
        case ErrorCode.NotUnique:
        case ErrorCode.ProfileNameNotUnique:
        case ErrorCode.AssessmentNameNotUnique:
          // NOTE: Frequent/expected cases, should already be handled
          //       elsewhere such as form validation.
          return
        case ErrorCode.ProfileNotFound: {
          // LH-1827
          toast.error('Filter Profile Not Found')

          return
        }
      }

      const errorContents = []

      if (includeApiDetails && source != null) {
        errorContents.push(source)
      }

      if (includeApiDetails) {
        errorContents.push(`Error code: ${e.code}`)
      }

      errorContents.push(
        ...[e.title, e.detail].filter(
          (content) => content != null && content !== ''
        )
      )

      toast.error(errorContents.join('\n\n'))
    })
  } else {
    const errorContents = []

    if (includeApiDetails && source != null) {
      errorContents.push(source)
    }

    errorContents.push(JSON.stringify(errors))
    toast.error(errorContents.join('\n\n'))
  }
}

export default notificationMiddleware
